import { FC, ReactNode, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Flex, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import Button from 'components/_core/Button';
import SunshineCard from 'components/_core/SunshineCard';

import locales from './locales';

export type DocumentCheck = {
  key: string;
  label: string;
  description?: ReactNode;
};

/**
 * A list of user prompts that they have to validate to verify a document.
 */
const DocumentChecklist: FC<{
  checks: DocumentCheck[];
  isLoading: boolean;
  onSubmit: () => void;
}> = ({ checks, isLoading, onSubmit }) => {
  /**
   * A simple validation schema with a boolean that has to be true for each check.
   */
  const schema = useMemo(
    () =>
      z.object(
        Object.fromEntries(
          checks.map(({ key }) => [key, z.literal<boolean>(true)]),
        ),
      ),
    [checks],
  );
  const defaultValues = useMemo(
    () => Object.fromEntries(checks.map(({ key }) => [key, false])),
    [checks],
  );

  const { control, formState, handleSubmit } = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack align="stretch" spacing="space-16">
        {checks.map((check) => (
          <Controller
            control={control}
            key={check.key}
            name={check.key}
            render={({ field }) => (
              <SunshineCard
                isChecked={field.value}
                onChange={field.onChange}
                title={check.label}
                variant="checkbox"
                width="auto"
              >
                {check.description ? (
                  <SunshineCard.Content>
                    <SunshineCard.Text>{check.description}</SunshineCard.Text>
                  </SunshineCard.Content>
                ) : null}
              </SunshineCard>
            )}
          />
        ))}
      </VStack>

      <Flex align="center" justify="flex-end" marginTop="space-40">
        <Button
          isDisabled={isLoading || !formState.isValid}
          isLoading={isLoading}
          type="submit"
          variant="primary"
        >
          {locales.validate}
        </Button>
      </Flex>
    </form>
  );
};

export default DocumentChecklist;
